import { lazy, Suspense, useMemo, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  RouterProvider,
} from "@tanstack/react-router";
import { Login } from "./login";
import { AppShell } from "./shell";
import { ActionsPage } from "./pages/actions";
import { AskPage } from "./pages/ask";
import { EvidencePage } from "./pages/evidence";
import { SignalsPage } from "./pages/signals";
import { VoicePage } from "./pages/voice";

const OverviewPage = lazy(() =>
  import("./pages/overview").then((module) => ({
    default: module.OverviewPage,
  })),
);
const DemandPage = lazy(() =>
  import("./pages/drivers").then((module) => ({
    default: module.DemandPage,
  })),
);
const FailureDemandPage = lazy(() =>
  import("./pages/failure-demand").then((module) => ({
    default: module.FailureDemandPage,
  })),
);
const RootCausesPage = lazy(() =>
  import("./pages/root-causes").then((module) => ({
    default: module.RootCausesPage,
  })),
);
const JourneysPage = lazy(() =>
  import("./pages/journeys").then((module) => ({
    default: module.JourneysPage,
  })),
);
const DataHealthPage = lazy(() =>
  import("./pages/data-health").then((module) => ({
    default: module.DataHealthPage,
  })),
);
const TaxonomyPage = lazy(() =>
  import("./pages/taxonomy").then((module) => ({
    default: module.TaxonomyPage,
  })),
);
const ReviewQueuePage = lazy(() =>
  import("./pages/review-queue").then((module) => ({
    default: module.ReviewQueuePage,
  })),
);
const MetricDefinitionsPage = lazy(() =>
  import("./pages/metric-definitions").then((module) => ({
    default: module.MetricDefinitionsPage,
  })),
);
const SettingsPage = lazy(() =>
  import("./pages/settings").then((module) => ({
    default: module.SettingsPage,
  })),
);

function makeRouter(onLogout: () => void) {
  const rootRoute = createRootRoute({
    component: () => <AppShell onLogout={onLogout} />,
  });
  const routes = [
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/",
      component: () => <Navigate to="/overview" />,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/overview",
      component: OverviewPage,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/signals",
      component: SignalsPage,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/voice",
      component: VoicePage,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/demand",
      component: DemandPage,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/drivers",
      component: () => <Navigate to="/demand" />,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/failure-demand",
      component: FailureDemandPage,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/root-causes",
      component: RootCausesPage,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/journeys",
      component: JourneysPage,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/evidence",
      component: EvidencePage,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/actions",
      component: ActionsPage,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/ask",
      component: AskPage,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/data-health",
      component: DataHealthPage,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/taxonomy",
      component: TaxonomyPage,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/review-queue",
      component: ReviewQueuePage,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/metric-definitions",
      component: MetricDefinitionsPage,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/settings",
      component: SettingsPage,
    }),
  ];
  return createRouter({
    routeTree: rootRoute.addChildren(routes),
    defaultPreload: "intent",
  });
}

export function App() {
  const [authenticated, setAuthenticated] = useState(
    () => window.localStorage.getItem("vox-demo-session") === "active",
  );
  const [queryClient] = useState(
    () =>
      new QueryClient({ defaultOptions: { queries: { staleTime: Infinity } } }),
  );
  const router = useMemo(() => makeRouter(() => setAuthenticated(false)), []);
  if (!authenticated)
    return <Login onAuthenticated={() => setAuthenticated(true)} />;
  return (
    <QueryClientProvider client={queryClient}>
      <Suspense
        fallback={
          <div className="route-loading" aria-label="Loading intelligence" />
        }
      >
        <RouterProvider router={router} />
      </Suspense>
    </QueryClientProvider>
  );
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof makeRouter>;
  }
}
